/**
 * PremiumRequired — Premium-Guard-Komponente mit Demo-Start (W3.1/W3.2)
 *
 * Zeigt Premium-Hinweis fuer Free-User.
 * Enthaelt "Demo starten"-Button zur Aktivierung des Demo-Modus (W3.1).
 * Enthaelt "Mehr ueber Premium erfahren"-Link zu /premium (W3.2).
 * Fuer Premium-User nicht sichtbar (API gibt 402 nur bei Free-Usern).
 *
 * Finding 1: Der Demo-Start bleibt optional im Kontext der aufrufenden Seite
 * (onDemoStart) statt hart nach /dashboard zu navigieren; die betroffenen
 * Queries werden gezielt invalidiert (demoQueryKey) statt den gesamten Cache
 * zu leeren.
 */
import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useDemoMode } from '../hooks/useDemoMode'

interface PremiumRequiredProps {
  message?: string
  /**
   * Wird nach erfolgreichem Demo-Start statt der Navigation zu /dashboard
   * aufgerufen (z. B. refetch der Seiten-Query) — der User bleibt im Kontext.
   */
  onDemoStart?: () => void
  /**
   * Query-Key, der nach dem Demo-Start gezielt invalidiert wird (z. B.
   * ['reports', 'monthly']). Ohne Key wird der komplette Cache geleert,
   * damit 402-Fehlerzustaende nicht bestehen bleiben.
   */
  demoQueryKey?: readonly unknown[]
  'data-testid'?: string
}

export function PremiumRequired({
  message,
  onDemoStart,
  demoQueryKey,
  'data-testid': testId = 'premium-required',
}: PremiumRequiredProps) {
  const { t } = useTranslation('components')
  const { activate } = useDemoMode()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [demoUnavailable, setDemoUnavailable] = useState(false)
  const resolvedMessage = message ?? t('premiumRequired.defaultMessage')

  function handleStartDemo() {
    // Ohne konfigurierten Demo-Token wird der Modus nicht aktiviert — die
    // bestehende Session bleibt unangetastet.
    if (!activate()) {
      setDemoUnavailable(true)
      return
    }
    setDemoUnavailable(false)

    if (demoQueryKey) {
      void queryClient.invalidateQueries({ queryKey: demoQueryKey })
    } else {
      queryClient.clear()
    }

    if (onDemoStart) {
      onDemoStart()
      return
    }
    void navigate({ to: '/dashboard' })
  }

  return (
    <div
      className="empty-state"
      data-testid={testId}
      role="alert"
      style={{ padding: 32, textAlign: 'center' }}
    >
      <h2 style={{ marginBottom: 8 }}>{t('premiumRequired.heading')}</h2>
      <p style={{ color: 'var(--text-subtle)', marginBottom: 24 }}>{resolvedMessage}</p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to="/plans" className="btn btn-primary">
          {t('premiumRequired.upgrade')}
        </Link>
        <button
          className="btn btn-ghost"
          onClick={handleStartDemo}
          data-testid="demo-start-button"
        >
          {t('premiumRequired.startDemo')}
        </button>
      </div>
      <p
        data-testid="demo-hint"
        style={{ marginTop: 12, fontSize: 13, color: 'var(--text-subtle)' }}
      >
        {t('premiumRequired.demoHint')}
      </p>
      {demoUnavailable && (
        <p
          role="status"
          data-testid="demo-unavailable"
          style={{ marginTop: 8, fontSize: 13, color: 'var(--danger, #ef4444)' }}
        >
          {t('premiumRequired.demoUnavailable')}
        </p>
      )}
      <Link
        to="/plans"
        className="link-secondary"
        data-testid="premium-learn-more"
        style={{
          display: 'inline-block',
          marginTop: 16,
          fontSize: 13,
          color: 'var(--text-subtle)',
          textDecoration: 'underline',
        }}
      >
        {t('premiumRequired.learnMore')}
      </Link>
    </div>
  )
}
